import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './Recaptcha.module.css';

/** A parte da API do reCAPTCHA v2 que o app usa. */
interface Grecaptcha {
  ready(callback: () => void): void;
  render(
    container: HTMLElement,
    parameters: {
      sitekey: string;
      theme?: 'light' | 'dark';
      size?: 'normal' | 'compact';
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    },
  ): number;
  reset(widgetId?: number): void;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

/** `render=explicit`: o app decide quando e onde desenhar a caixa (e pode refazê-la). `hl`: português. */
export const RECAPTCHA_SCRIPT_URL =
  'https://www.google.com/recaptcha/api.js?render=explicit&hl=pt-BR';
const LOAD_TIMEOUT_MS = 15_000;

let loading: Promise<Grecaptcha> | null = null;

/** Carrega o script da Google uma vez. Em caso de falha, a próxima chamada tenta de novo. */
export function loadRecaptcha(timeoutMs: number = LOAD_TIMEOUT_MS): Promise<Grecaptcha> {
  if (window.grecaptcha?.render) {
    const ready = window.grecaptcha;
    return new Promise((resolve) => ready.ready(() => resolve(ready)));
  }
  if (loading) return loading;
  loading = new Promise<Grecaptcha>((resolve, reject) => {
    const script = document.createElement('script');
    const fail = (reason: string): void => {
      window.clearTimeout(timer);
      script.remove();
      loading = null;
      reject(new Error(reason));
    };
    const timer = window.setTimeout(() => fail('timeout'), timeoutMs);
    script.src = RECAPTCHA_SCRIPT_URL;
    script.async = true;
    script.onerror = () => fail('script');
    script.onload = () => {
      const g = window.grecaptcha;
      if (!g) return fail('indisponível');
      g.ready(() => {
        window.clearTimeout(timer);
        // Carregado: dali em diante `window.grecaptcha` responde sozinho (primeiro `if` da função).
        loading = null;
        resolve(g);
      });
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface RecaptchaHandle {
  /** Apaga a marcação: a resposta vale uma vez só, então depois de cada tentativa é preciso marcar de novo. */
  reset(): void;
}

interface RecaptchaProps {
  siteKey: string;
  /** A resposta quando a pessoa resolve o desafio; `null` quando ela expira ou o desafio falha. */
  onChange: (token: string | null) => void;
}

/** A caixa "Não sou um robô" (reCAPTCHA v2) da Google. */
export const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(function Recaptcha(
  { siteKey, onChange },
  ref,
) {
  const host = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);
  const grecaptcha = useRef<Grecaptcha | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // O callback da Google guarda a função do primeiro `render`: aqui ela sempre chama a versão atual.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        if (grecaptcha.current && widgetId.current !== null) {
          grecaptcha.current.reset(widgetId.current);
        }
        onChangeRef.current(null);
      },
    }),
    [],
  );

  useEffect(() => {
    const container = host.current;
    if (!container) return;
    let cancelled = false;
    // Um elemento novo a cada montagem: a Google recusa desenhar duas vezes no mesmo (modo estrito do React).
    const box = document.createElement('div');
    container.appendChild(box);
    loadRecaptcha().then(
      (g) => {
        if (cancelled) return;
        grecaptcha.current = g;
        widgetId.current = g.render(box, {
          sitekey: siteKey,
          theme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
          size: window.innerWidth < 360 ? 'compact' : 'normal',
          callback: (token) => onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current(null),
          'error-callback': () => onChangeRef.current(null),
        });
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
      grecaptcha.current = null;
      widgetId.current = null;
      box.remove();
    };
  }, [siteKey, attempt]);

  const retry = useCallback(() => {
    setFailed(false);
    setAttempt((n) => n + 1);
  }, []);

  return (
    <div className={styles.box} role="group" aria-label="Verificação de segurança">
      <div ref={host} className={styles.widget} />
      {failed && (
        <p role="alert" className={styles.failure}>
          Não foi possível carregar a verificação de segurança. Confira a conexão (ou um bloqueador
          de anúncios) e{' '}
          <button type="button" className={styles.retry} onClick={retry}>
            tente de novo
          </button>
          .
        </p>
      )}
    </div>
  );
});
